import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { collectServiceEntries } from './FunctionSheetRenderer';
import { eventSpansDate, getEventDayCount, fmt } from '../lib/utils';
import { toLocalDateKey, addDays, parseLocalDate } from '../lib/dateHelpers';
import { useOccupancy, formatOcc } from '../lib/occupancy';
import { needsDayBeforeOrder, packageLabel } from '../lib/packages';
import { hasDietaryAnswer, getTickConflicts, effectiveFoodTime } from '../lib/eventCompleteness';

/**
 * Function Sheet v2 (Oct 2026) — daily and weekly print share these blocks.
 * Built for kitchen / restaurant / ops:
 * summary · kitchen covers-by-time · afternoon tea (only when there is one) ·
 * one compact block per event · restaurant & bar list with packages · spa.
 * Daily auto-scales to one A4 page. Weekly flows day after day.
 * Class names use a "ds-" prefix: App.css print rules target Tailwind names
 * like "fixed"/"flex-col", so this sheet uses its own scoped CSS.
 */

const HIDDEN_STAGES = new Set(['archived', 'lost', 'cancelled']);
const TABLE_HIDDEN = new Set(['cancelled', 'linked_to_event', 'no_show']);
const AREA_SHORT = { randr: 'R&R', brewery: 'Brewery', reynards: 'Reynards' };
const BRUNCH_RE = /brunch/i;

const hhmm = (t) => (typeof t === 'string' ? t.slice(0, 5) : '');
const lines = (txt) => String(txt || '').split(/\r?\n/).map((l) => l.replace(/^[\s•\-*•]+/, '').trim()).filter(Boolean);
const num = (v) => { const n = parseInt(v, 10); return Number.isFinite(n) && n > 0 ? n : 0; };
const money = (v) => (v ? fmt(v).replace(/\.00$/, '') : '');
const validTable = (b) => !TABLE_HIDDEN.has((b.status || '').toLowerCase());
const nameWords = (s) => String(s || '').toLowerCase().replace(/[^a-z\u00c0-\u024f' ]/g, ' ').split(/\s+/)
  .filter((w) => w && !['mr', 'mrs', 'ms', 'miss', 'dr', 'and', '&', 'x'].includes(w) && !/^\d+$/.test(w));
const mins = (t) => { const m = /^(\d{1,2}):(\d{2})/.exec(t || ''); return m ? (+m[1]) * 60 + (+m[2]) : null; };
/** Same guest? Full name match, or same surname with a table within 3h of the brunch (or a brunch package). */
function sameGuest(table, spa, brunchTime) {
  const a = nameWords(table.guestName), b = nameWords(spa.guestName);
  if (!a.length || !b.length) return false;
  if (a.join(' ') === b.join(' ')) return true;
  if (a[a.length - 1] !== b[b.length - 1]) return false;
  if (/brunch/i.test(table.packageName || '')) return true;
  const tm = mins(hhmm(table.time)), bm = mins(brunchTime);
  return tm !== null && bm !== null && Math.abs(tm - bm) <= 180;
}

function dayIndex(evt, dateStr) {
  const a = parseLocalDate(evt.date), b = parseLocalDate(dateStr);
  if (!a || !b) return 1;
  return Math.round((b - a) / 86400000) + 1;
}

/** "2 hour spa and brunch" at 11:00 → brunch after the spa, ~13:00 */
function brunchTimeAfterSpa(spa) {
  const t = hhmm(spa.time);
  const text = `${spa.details || ''} ${spa.comments || ''}`;
  const hrs = /(\d+(?:\.\d+)?)\s*(?:h|hr|hrs|hour|hours)\b/i.exec(text);
  const mns = /(\d{2,3})\s*(?:m|min|mins|minutes)\b/i.exec(text);
  // duration defaults to 60 in the DB, so only trust it when set to something else
  const dur = hrs ? Math.round(parseFloat(hrs[1]) * 60) : mns ? parseInt(mns[1], 10) : (spa.duration && spa.duration !== 60 ? spa.duration : 0);
  if (!t || !dur) return t;
  const [h, mi] = t.split(':').map(Number);
  const total = h * 60 + mi + dur;
  return `${String(Math.floor(total / 60) % 24).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/** Everything one day needs, from the raw app data. Pure — no fetching. */
export function buildDay(dateStr, { events = [], tableBookings = [], spaBookings = [], packages = [], venues = [] }) {
  const venueName = (id) => (venues.find((v) => v.id === id)?.name) || AREA_SHORT[id] || id || '';
  const area = (id) => AREA_SHORT[id] || venueName(id);
  const tomorrowStr = toLocalDateKey(addDays(dateStr, 1));

  // A tick that says "no food" / "music only" is overruled when the event's
  // own service lines say otherwise (ESB Network, 30 Oct 2026) — the event
  // stays a food event for the kitchen and carries a warning.
  const allDayEvents = events
    .filter((e) => eventSpansDate(e, dateStr) && !HIDDEN_STAGES.has(e.stage))
    .map((e) => {
      const c = getTickConflicts(e);
      if (!c.strong.length) return e;
      // Display-only copy. _raw is the stored event — anything that opens an
      // editor must use it so the overruled tick is never saved back.
      return {
        ...e,
        _raw: e,
        tickConflicts: c.strong,
        musicOnly: false,
        foodServiceTime: e.foodServiceTime === 'none' ? (c.foodTime || e.startTime || '') : e.foodServiceTime,
      };
    })
    .sort((a, b) => (a.startTime || '').localeCompare(b.startTime || ''));
  // Cancelled / lost / archived bookings on this date. Never silently gone:
  // every view built on buildDay shows them as one greyed line with the reason.
  const offEvents = events.filter((e) => eventSpansDate(e, dateStr) && HIDDEN_STAGES.has(e.stage));
  // "Music booking only" with no food time = nice-to-know, not a function.
  // Kept out of event lists, counts and covers; shown as one small line.
  // A music booking WITH a food time stays a normal event for the kitchen.
  const dayEvents = allDayEvents.filter((e) => !isMusicNoFood(e));
  const musicEvents = allDayEvents.filter(isMusicNoFood);
  const realTables = tableBookings.filter((b) => b.date === dateStr && validTable(b));
  const daySpa = spaBookings
    .filter((b) => b.date === dateStr)
    .sort((a, b) => (a.time || '').localeCompare(b.time || ''));

  // Spa & brunch bookings made in the spa list become a restaurant slot too,
  // unless reception already made a matching table booking for the same guest.
  const spaBrunch = daySpa
    .filter((s) => BRUNCH_RE.test(`${s.details || ''} ${s.comments || ''}`))
    .filter((s) => !realTables.some((t) => sameGuest(t, s, brunchTimeAfterSpa(s))))
    .map((s) => ({
      id: `spa-${s.id}`,
      fromSpa: true,
      spaTime: hhmm(s.time),
      time: brunchTimeAfterSpa(s),
      venue: 'randr',
      guestName: s.guestName,
      covers: s.covers || 1,
      packageName: 'Brunch',
      notes: s.comments || '',
    }));
  // Events held in a restaurant area (e.g. a party upgraded from a table
  // booking that still sits in R&R) also appear in the restaurant list so the
  // floor team sees the space is taken. Their covers are already counted by
  // the event itself, so these rows are left out of table totals.
  const restaurantEvents = dayEvents
    .filter((e) => AREA_SHORT[e.venue_id] && e.type !== 'music_night')
    .map((e) => ({
      id: `evt-${e.id}`,
      isEvent: true,
      time: hhmm(e.startTime),
      endTime: hhmm(e.endTime),
      venue: e.venue_id,
      guestName: (e.name || '').replace(/\s*\(from table\)\s*$/i, ''),
      covers: e.guests || 0,
    }));
  const dayTables = [...realTables, ...spaBrunch, ...restaurantEvents].sort((a, b) => (a.time || '').localeCompare(b.time || ''));

  const teaToday = dayTables.filter((b) => !b.fromSpa && !b.isEvent && needsDayBeforeOrder(b, packages));
  const teaTomorrow = tableBookings
    .filter((b) => b.date === tomorrowStr && validTable(b) && needsDayBeforeOrder(b, packages))
    .sort((a, b) => (a.time || '').localeCompare(b.time || ''));

  // Events marked "No food" (drinks / room only, e.g. a party moving on to the
  // New Bar after dinner) stay on the sheet but are left out of food covers.
  const noFood = (e) => e.foodServiceTime === 'none';
  const eventCovers = dayEvents.reduce((s, e) => s + (noFood(e) ? 0 : (e.guests || 0)), 0);
  const tableCovers = dayTables.reduce((s, b) => s + (b.isEvent ? 0 : (b.covers || 0)), 0);
  const tableCount = dayTables.filter((b) => !b.isEvent).length;
  const spaGuests = daySpa.reduce((s, b) => s + (b.covers || 1), 0);

  const m = new Map();
  const add = (t, n, what) => {
    const k = hhmm(t) || '—';
    const cur = m.get(k) || { covers: 0, what: [] };
    cur.covers += n || 0;
    if (what && !cur.what.includes(what)) cur.what.push(what);
    m.set(k, cur);
  };
  dayTables.filter((b) => !b.isEvent).forEach((b) => add(b.time, b.covers, b.fromSpa ? 'Spa brunch' : area(b.venue)));
  dayEvents.filter((e) => !noFood(e)).forEach((e) => add(effectiveFoodTime(e) || e.startTime, e.guests, (e.name || 'Event').trim().split(/\s+/).slice(0, 2).join(' ')));
  const slots = [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]));

  return { dateStr, tomorrowStr, dayEvents, musicEvents, offEvents, dayTables, tableCount, daySpa, teaToday, teaTomorrow, eventCovers, tableCovers, spaGuests, slots, venueName, area };
}

function KitchenStrip({ slots }) {
  if (!slots.length) return null;
  return (
    <>
      <div className="ds-h2">Kitchen: covers by time</div>
      <table className="ds-tl">
        <tbody>
          <tr><th className="ds-lbl">Time</th>{slots.map(([t]) => <th key={t}>{t}</th>)}</tr>
          <tr><td className="ds-lbl">Covers</td>{slots.map(([t, s]) => <td key={t} className={s.covers >= 20 ? 'ds-big' : ''}>{s.covers}</td>)}</tr>
          <tr><td className="ds-lbl">What</td>{slots.map(([t, s]) => <td key={t}>{s.what.slice(0, 2).join(', ')}{s.what.length > 2 ? ` +${s.what.length - 2}` : ''}</td>)}</tr>
        </tbody>
      </table>
    </>
  );
}

/** Afternoon tea boxes — each shown only when there is something in it. */
function TeaBoxes({ day }) {
  const { teaToday, teaTomorrow, tomorrowStr, area } = day;
  if (!teaToday.length && !teaTomorrow.length) return null;
  const tomorrowLabel = parseLocalDate(tomorrowStr)?.toLocaleDateString('en-IE', { weekday: 'short', day: 'numeric', month: 'short' });
  return (
    <div className="ds-kg">
      {teaToday.length > 0 && (
        <div className="ds-tea">
          <div className="ds-l2">Kitchen today: afternoon tea</div>
          {teaToday.map((b) => (
            <div key={b.id}><span className="ds-chip">AFT. TEA</span><b>{hhmm(b.time)} · {b.covers}</b> · {(b.guestName || '').trim()} · {area(b.venue)}</div>
          ))}
        </div>
      )}
      {teaTomorrow.length > 0 && (
        <div className="ds-ahead">
          <div className="ds-l3">⚠ Order today: afternoon tea for tomorrow, {tomorrowLabel}</div>
          {teaTomorrow.map((b) => (
            <div key={b.id}><b>{hhmm(b.time)} · {b.covers} × {b.packageName || 'Afternoon Tea'}</b> · {(b.guestName || '').trim()} · {area(b.venue)}{b.roomNumber ? ' · in-house' : ''}</div>
          ))}
        </div>
      )}
    </div>
  );
}

export function isMusicNoFood(e) {
  return !!e?.musicOnly && !(e.foodServiceTime && e.foodServiceTime !== 'none');
}

/** One low-key line for music-only bookings (no food, no function). */
export function musicLineText(list, venueName) {
  return list.map((e) => [
    [hhmm(e.startTime), hhmm(e.endTime)].filter(Boolean).join('–'),
    (e.name || '').replace(/\s*\(from table\)\s*$/i, '').trim(),
    venueName ? venueName(e.venue_id) : '',
  ].filter(Boolean).join(' ')).join(' · ');
}

function MusicLine({ list, venueName }) {
  if (!list || !list.length) return null;
  return <p className="ds-music">♪ Music only (no food): {musicLineText(list, venueName)}</p>;
}

const STAGE_WORD = { cancelled: 'cancelled', lost: 'marked lost', archived: 'archived' };
/** One greyed line naming bookings that are on this date but not running. */
export function offLineText(list) {
  return list.map((e) => `${(e.name || 'Event').trim()} (${STAGE_WORD[e.stage] || e.stage})`).join(' · ');
}

function OffLine({ list }) {
  if (!list || !list.length) return null;
  return <p className="ds-music">Not running – {offLineText(list)}. If this is wrong, fix the status in Events.</p>;
}

function EventBlock({ e, dateStr, payments, venueName }) {
  const total = getEventDayCount(e);
  const di = total > 1 ? dayIndex(e, dateStr) : 0;
  const dep = payments.filter((p) => p.event_id === e.id).reduce((s, p) => s + (p.amount || 0), 0) || e.depositPaid || 0;
  const couple = [e.brideName, e.groomName].filter(Boolean).join(' & ');
  const priceBits = [
    e.pricePerHead ? `${money(e.pricePerHead)} pp` : '',
    e.packageTotal ? `package ${money(e.packageTotal)}` : '',
    e.roomHire ? `room hire ${money(e.roomHire)}` : '',
    dep > 0 ? `deposit paid ${money(dep)}` : 'no deposit recorded',
  ].filter(Boolean);
  const services = collectServiceEntries(e);
  const run = lines(e.setupRequirements);
  const notes = lines(e.notes);
  const menu = lines(e.menuNotes);
  const dietBits = [
    num(e.dietaryVeg) && `${num(e.dietaryVeg)} vegetarian`,
    num(e.dietaryVegan) && `${num(e.dietaryVegan)} vegan`,
    num(e.dietaryCoeliac) && `${num(e.dietaryCoeliac)} coeliac/GF`,
    e.dietaryAllergens && `Allergens: ${e.dietaryAllergens}`,
    e.dietaryRequirements,
  ].filter(Boolean);
  const flow = (arr) => <p className="ds-flow">{arr.map((l, i) => <span key={i}>{i > 0 && <span className="sep">•</span>}{l}</span>)}</p>;
  return (
    <div className="ds-evt">
      <div className="ds-eh">
        <span className="t">{[hhmm(e.startTime), hhmm(e.endTime)].filter(Boolean).join('–') || '—'}</span>
        <span className="n">{e.type === 'wedding' ? '💒 ' : ''}{(e.name || '').trim()}{di ? ` (Day ${di} of ${total})` : ''}</span>
        <span className="m">{venueName(e.venue_id)}{e.setup ? ` · ${e.setup.charAt(0).toUpperCase()}${e.setup.slice(1)}` : ''}{e.foodServiceTime === 'none' ? ' · no food' : effectiveFoodTime(e) ? ` · food ${hhmm(effectiveFoodTime(e))}` : ''}</span>
        <span className="cv">{e.guests || 0} {e.foodServiceTime === 'none' ? 'guests' : 'covers'}</span>
      </div>
      {e.tickConflicts && e.tickConflicts.length > 0 && (
        <div className="ds-conflict">⚠ CHECK WITH RECEPTION: {e.tickConflicts.join('; ')}. Shown here as a food event.</div>
      )}
      <div className="ds-price">{priceBits.join(' · ')}{e.invoiceNumber ? ` · Inv ${e.invoiceNumber}` : ''}</div>
      <div className="ds-eb">
        <div className="ds-col">
          {services.length > 0 && (<>
            <div className="ds-lab">Service times</div>
            <p>{services.map((s, i) => <span key={i}>{i > 0 ? ' · ' : ''}<b>{s.time}</b> {s.description}{s.location ? ` (${s.location})` : ''}</span>)}</p>
          </>)}
          {run.length > 0 && (<>
            <div className="ds-lab">Running order / setup</div>
            <p className="ds-flow">{run.map((l, i) => <span key={i}><b className="nn">{i + 1}</b>{l} </span>)}</p>
          </>)}
          {notes.length > 0 && (<><div className="ds-lab">Notes</div>{flow(notes)}</>)}
          {e.equipment && e.equipment.length > 0 && <p><b>Equipment:</b> {e.equipment.join(', ')}</p>}
          {!services.length && !run.length && !notes.length && <p className="ds-muted">No setup or running order entered</p>}
        </div>
        <div className="ds-col">
          <div className="ds-lab">Menu</div>
          {menu.length > 0 ? flow(menu) : <p className="ds-muted">No menu entered</p>}
          {dietBits.length > 0
            ? <div className="ds-diet">⚠ {dietBits.join(' · ')}</div>
            : e.dietaryNA ? <div className="ds-diet none">Dietary: N/A – no meal chosen</div>
            : hasDietaryAnswer(e) ? <div className="ds-diet none">No dietary requirements</div>
            : <div className="ds-diet none">No dietary info recorded</div>}
          <div className="ds-contact">
            {couple && <>{couple} · </>}
            Contact: {[e.client, e.clientPhone, e.clientEmail].filter(Boolean).join(' · ') || '—'}
          </div>
        </div>
      </div>
    </div>
  );
}

function TableList({ day, payments, packages, showEmpty }) {
  const { dayTables, tableCovers, tableCount, area } = day;
  const tableDeposit = (b) => {
    if (b.fromSpa) return 0;
    const ps = payments.filter((p) => p.bookingId === b.id || p.booking_id === b.id);
    return ps.length ? ps.reduce((s, p) => s + (p.amount || 0), 0) : (b.depositAmount || 0);
  };
  if (!dayTables.length && !showEmpty) return null;
  return (
    <>
      <div className="ds-h2">Restaurant &amp; bar bookings ({tableCount} · {tableCovers} covers{dayTables.length > tableCount ? ` + ${dayTables.length - tableCount} event${dayTables.length - tableCount > 1 ? 's' : ''}` : ''})</div>
      {dayTables.length === 0 ? <p className="ds-empty">No table bookings</p> : (
        <table className="ds-tb">
          <colgroup><col style={{ width: '11mm' }} /><col style={{ width: '17mm' }} /><col style={{ width: '38mm' }} /><col style={{ width: '9mm' }} /><col style={{ width: '9mm' }} /><col style={{ width: '50mm' }} /><col /><col style={{ width: '11mm' }} /></colgroup>
          <thead><tr><th>Time</th><th>Area</th><th>Guest</th><th>Cvrs</th><th>Table</th><th>Package / included</th><th>Notes</th><th>Dep.</th></tr></thead>
          <tbody>
            {dayTables.map((b) => {
              if (b.isEvent) {
                return (
                  <tr key={b.id} className="evt">
                    <td className="time">{b.time}</td>
                    <td><b>{area(b.venue)}</b></td>
                    <td><span className="ds-chip evt">EVENT</span>{b.guestName}</td>
                    <td className="num">{b.covers}</td>
                    <td></td>
                    <td><span className="ds-pkg">Private event</span></td>
                    <td>{b.time}{b.endTime ? `–${b.endTime}` : ''} in {area(b.venue)} · full details under Events above</td>
                    <td></td>
                  </tr>
                );
              }
              const pk = packageLabel(b, packages);
              const tea = !b.fromSpa && needsDayBeforeOrder(b, packages);
              const note = [
                b.fromSpa && `From spa booking (spa ${b.spaTime}) · brunch after spa`,
                b.specialRequirements, b.notes, b.partyComposition && `Party: ${b.partyComposition}`,
                b.roomNumber && `Room ${b.roomNumber}`, (b.status || '').toLowerCase() === 'waitlist' && 'WAITLIST',
              ].filter(Boolean).join(' · ');
              const dep = tableDeposit(b);
              return (
                <tr key={b.id} className={tea ? 'tea' : b.fromSpa ? 'spa' : (b.covers >= 10 ? 'large' : '')}>
                  <td className="time">{b.fromSpa ? '~' : ''}{hhmm(b.time)}</td>
                  <td><b>{area(b.venue)}</b></td>
                  <td>{tea && <span className="ds-chip">AFT. TEA</span>}{(b.guestName || '').trim()}</td>
                  <td className="num">{b.covers}</td>
                  <td>{b.tableNumber || ''}</td>
                  <td>{pk.name ? <span className="ds-pkg">{pk.name}{pk.detail && <small>{pk.detail}</small>}</span> : <span className="ds-muted">À la carte</span>}</td>
                  <td>{note}</td>
                  <td>{dep > 0 ? money(dep) : ''}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}

function SpaList({ day }) {
  const { daySpa, spaGuests } = day;
  if (!daySpa.length) return null;
  return (
    <>
      <div className="ds-h2">Spa ({daySpa.length} · {spaGuests} guests)</div>
      <table className="ds-tb">
        <thead><tr><th style={{ width: '11mm' }}>Time</th><th style={{ width: '40mm' }}>Guest</th><th>Treatment</th><th style={{ width: '9mm' }}>Gsts</th><th>Comments</th></tr></thead>
        <tbody>
          {daySpa.map((b) => (
            <tr key={b.id}><td className="time">{hhmm(b.time)}</td><td>{b.guestName}</td><td>{b.details}</td><td className="num">{b.covers || ''}</td><td>{b.comments}</td></tr>
          ))}
        </tbody>
      </table>
    </>
  );
}

function SummaryStrip({ cells }) {
  return (
    <div className="ds-sum">
      {cells.map(([v, label], i) => <div key={i}><b>{v}</b><span>{label}</span></div>)}
    </div>
  );
}

// In-house tonight: big number = guests, label spells out rooms so it can't be misread.
const occCell = (o) => (o ? [o.guests, `Guests in-house · ${o.rooms} room${o.rooms === 1 ? '' : 's'}`] : ['—', o === null ? 'In-house: not entered' : 'In-house']);
const printedAt = () => new Date().toLocaleString('en-IE', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

/* ───────────────────────── DAILY ───────────────────────── */
export default function DailySheetV2({ date, events = [], tableBookings = [], spaBookings = [], venues = [], payments = [], packages = [] }) {
  const dateStr = toLocalDateKey(date);
  const day = useMemo(
    () => buildDay(dateStr, { events, tableBookings, spaBookings, packages, venues }),
    [dateStr, events, tableBookings, spaBookings, packages, venues]
  );
  const { occ: occMap } = useOccupancy([dateStr]);
  const occ = occMap[dateStr];

  // Auto-fit to one A4 page: printable area is 192mm x 278mm. If the day's
  // content is taller, shrink it (never below 82%) so it still prints on one page.
  const rootRef = useRef(null);
  const wrapRef = useRef(null);
  const [zoom, setZoom] = useState(1);
  // Runs after every render on purpose (content changes with live data);
  // setZoom bails out when the value is unchanged, so this cannot loop.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useLayoutEffect(() => {
    const el = rootRef.current, wrap = wrapRef.current;
    if (!el || !wrap) return;
    wrap.style.zoom = '1';
    const pagePx = 278 * 3.7795 * 0.985;
    const h = el.getBoundingClientRect().height;
    const z = h > pagePx ? Math.max(0.82, Math.floor((pagePx / h) * 100) / 100) : 1;
    wrap.style.zoom = String(z);
    if (z !== zoom) setZoom(z);
  });

  const longDate = parseLocalDate(dateStr)?.toLocaleDateString('en-IE', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });

  return (
    <div className="ds-wrap" ref={wrapRef}>
      <div className="ds" ref={rootRef}>
        <style>{CSS}</style>
        <div className="ds-hdr">
          <div><h1>{longDate}</h1><div className="ds-sub">Errigal Country House Hotel · Daily Function Sheet</div></div>
          <div className="ds-right">Printed {printedAt()}</div>
        </div>
        <SummaryStrip cells={[
          [day.dayEvents.length, `Events · ${day.eventCovers} cvrs`],
          [day.tableCount, `Tables · ${day.tableCovers} cvrs`],
          [day.eventCovers + day.tableCovers, 'Food covers'],
          occCell(occ),
          [day.spaGuests, `Spa guests · ${day.daySpa.length} bkgs`],
        ]} />
        <KitchenStrip slots={day.slots} />
        <TeaBoxes day={day} />
        <div className="ds-h2">Events ({day.dayEvents.length})</div>
        {day.dayEvents.length === 0 && <p className="ds-empty">No events today</p>}
        {day.dayEvents.map((e) => <EventBlock key={e.id} e={e} dateStr={dateStr} payments={payments} venueName={day.venueName} />)}
        <MusicLine list={day.musicEvents} venueName={day.venueName} />
        <OffLine list={day.offEvents} />
        <TableList day={day} payments={payments} packages={packages} showEmpty />
        <SpaList day={day} />
        <div className="ds-foot">
          <span>In-house tonight: {formatOcc(occ)} &nbsp;|&nbsp; Spa: {day.daySpa.length} bookings · {day.spaGuests} guests</span>
          <span className="ds-muted">Changes after printing are in the app</span>
        </div>
        {zoom < 1 && <div className="ds-fit">Busy day: sheet scaled to {Math.round(zoom * 100)}% to fit one A4 page.</div>}
      </div>
    </div>
  );
}

/* ───────────────────────── WEEKLY ───────────────────────── */
/** Same blocks as the daily sheet, one section per day, Monday to Sunday. */
export function WeeklySheetV2({ weekStart, events = [], tableBookings = [], spaBookings = [], venues = [], payments = [], packages = [] }) {
  const startStr = toLocalDateKey(weekStart);
  const dateKeys = useMemo(() => Array.from({ length: 7 }, (_, i) => toLocalDateKey(addDays(startStr, i))), [startStr]);
  const days = useMemo(
    () => dateKeys.map((d) => buildDay(d, { events, tableBookings, spaBookings, packages, venues })),
    [dateKeys, events, tableBookings, spaBookings, packages, venues]
  );
  const { occ: occMap } = useOccupancy(dateKeys);
  const fmtD = (d, o) => parseLocalDate(d)?.toLocaleDateString('en-IE', o);
  const tot = days.reduce((a, d) => ({
    ev: a.ev + d.dayEvents.length, ec: a.ec + d.eventCovers, tb: a.tb + d.tableCount, tc: a.tc + d.tableCovers, spa: a.spa + d.spaGuests,
  }), { ev: 0, ec: 0, tb: 0, tc: 0, spa: 0 });
  const guestNights = dateKeys.reduce((s, d) => s + (occMap[d]?.guests || 0), 0);

  return (
    <div className="ds ds-week">
      <style>{CSS}</style>
      <div className="ds-hdr">
        <div>
          <h1>Week of {fmtD(dateKeys[0], { weekday: 'short', day: 'numeric', month: 'short' })} – {fmtD(dateKeys[6], { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' })}</h1>
          <div className="ds-sub">Errigal Country House Hotel · Weekly Function Sheet</div>
        </div>
        <div className="ds-right">Printed {printedAt()}</div>
      </div>
      <SummaryStrip cells={[
        [tot.ev, `Events · ${tot.ec} cvrs`],
        [tot.tb, `Tables · ${tot.tc} cvrs`],
        [tot.ec + tot.tc, 'Food covers'],
        [guestNights || '—', 'Guest nights in-house'],
        [tot.spa, 'Spa guests'],
      ]} />
      {days.map((d) => {
        const o = occMap[d.dateStr];
        const empty = !d.dayEvents.length && !d.musicEvents.length && !d.offEvents.length && !d.dayTables.length && !d.daySpa.length && !d.teaTomorrow.length;
        return (
          <section key={d.dateStr}>
            <div className="ds-day">
              <h2>{fmtD(d.dateStr, { weekday: 'long', day: 'numeric', month: 'long' })}</h2>
              <span>{d.dayEvents.length} events · {d.eventCovers} cvrs</span>
              <span>{d.tableCount} tables · {d.tableCovers} cvrs</span>
              <span className="ds-inhouse">In-house: {formatOcc(o)}</span>
              <span>Spa {d.spaGuests}</span>
            </div>
            {empty ? <p className="ds-empty">Nothing booked</p> : (<>
              <KitchenStrip slots={d.slots} />
              <TeaBoxes day={d} />
              {d.dayEvents.map((e) => <EventBlock key={e.id} e={e} dateStr={d.dateStr} payments={payments} venueName={d.venueName} />)}
              <MusicLine list={d.musicEvents} venueName={d.venueName} />
              <OffLine list={d.offEvents} />
              <TableList day={d} payments={payments} packages={packages} />
              <SpaList day={d} />
            </>)}
          </section>
        );
      })}
      <div className="ds-foot"><span>Week totals: {tot.ev} events · {tot.tb} table bookings · {tot.ec + tot.tc} food covers</span><span className="ds-muted">Changes after printing are in the app</span></div>
    </div>
  );
}

const CSS = `
@page { size: A4 portrait; margin: 9mm 9mm 10mm; }
.ds { width: 192mm; margin: 0 auto; font: 9.5pt/1.3 Arial, Helvetica, sans-serif; color: #111; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
.ds * { box-sizing: border-box; }
.ds-hdr { display: flex; justify-content: space-between; align-items: flex-end; border-bottom: 2px solid #111; padding-bottom: 3px; }
.ds-hdr h1 { font-size: 15pt; margin: 0; font-weight: 700; }
.ds-sub { font-size: 9pt; color: #444; }
.ds-right { text-align: right; font-size: 8.5pt; color: #444; }
.ds-sum { display: grid; grid-template-columns: repeat(5, 1fr); margin: 5px 0 4px; border: 1px solid #111; }
.ds-sum > div { padding: 2px 6px; border-right: 1px solid #111; }
.ds-sum > div:last-child { border-right: 0; }
.ds-sum b { font-size: 11pt; display: block; line-height: 1.1; }
.ds-sum span { font-size: 7.5pt; text-transform: uppercase; letter-spacing: .03em; color: #444; }
.ds-h2 { font-size: 8.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; margin: 5px 0 2px; break-after: avoid; page-break-after: avoid; }
.ds-tl, .ds-kg { break-inside: avoid; }
.ds table { border-collapse: collapse; width: 100%; }
.ds-tl td, .ds-tl th { border: 1px solid #999; text-align: center; padding: 1px 2px; font-size: 8.5pt; }
.ds-tl th, .ds-tl .ds-lbl { background: #f1f1f1; font-weight: 700; }
.ds-tl .ds-lbl { text-align: left; width: 14mm; }
.ds-tl td.ds-big { font-weight: 700; background: #fde7c4; }
.ds-kg { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; margin: 6px 0 0; break-inside: avoid; }
.ds-tea { border: 2px solid #db2777; background: #fdf2f8; padding: 3px 6px; font-size: 9pt; }
.ds-ahead { border: 2px dashed #b91c1c; background: #fef2f2; padding: 3px 6px; font-size: 9pt; }
.ds-l2 { font-size: 7.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #9d174d; }
.ds-l3 { font-size: 7.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #b91c1c; }
.ds-chip { display: inline-block; font-size: 7.5pt; font-weight: 700; letter-spacing: .04em; padding: 0 4px; border-radius: 2px; color: #fff; background: #db2777; margin-right: 3px; }
.ds-evt { border: 1.5px solid #111; margin-bottom: 5px; break-inside: avoid; page-break-inside: avoid; }
.ds-eh { display: flex; flex-wrap: wrap; gap: 2px 10px; align-items: baseline; background: #111; color: #fff; padding: 2px 6px; }
.ds-eh .t { font-weight: 700; font-size: 11pt; }
.ds-eh .n { font-weight: 700; font-size: 11pt; }
.ds-eh .m { font-size: 9pt; }
.ds-eh .cv { font-weight: 700; font-size: 11pt; margin-left: auto; white-space: nowrap; }
.ds-price { background: #f3f4f6; padding: 1px 6px; font-size: 8.5pt; border-bottom: 1px solid #999; }
.ds-eb { display: grid; grid-template-columns: 1.35fr 1fr; }
.ds-col { padding: 3px 6px; }
.ds-col + .ds-col { border-left: 1px solid #999; }
.ds-lab { font-size: 7.5pt; font-weight: 700; text-transform: uppercase; letter-spacing: .05em; color: #444; margin: 0 0 1px; }
.ds-col p { margin: 0 0 3px; }
.ds-flow { font-size: 9pt; line-height: 1.25; }
.ds-flow b.nn { display: inline-block; min-width: 11px; background: #111; color: #fff; text-align: center; border-radius: 2px; font-size: 7.5pt; margin-right: 2px; padding: 0 2px; }
.ds-flow .sep { color: #999; margin: 0 3px; }
.ds-fit { font-size: 7.5pt; color: #666; }
@media print { .ds-fit { display: none; } }
.ds ol, .ds ul { margin: 0 0 3px; padding-left: 14px; }
.ds li { margin: 0; }
.ds-diet { border: 1.5px solid #b91c1c; color: #b91c1c; padding: 1px 5px; font-weight: 700; margin: 2px 0 3px; }
.ds-music { color: #6b7280; font-size: 8.5pt; margin: 0 0 5px; }
.ds-conflict { border: 1.5px solid #b45309; background: #fef3c7; color: #78350f; font-weight: 700; font-size: 8.5pt; padding: 2px 5px; margin: 2px 0 3px; }
.ds-diet.none { border-color: #b45309; color: #b45309; font-weight: 600; }
.ds-contact { font-size: 8.5pt; color: #333; }
.ds-tb th, .ds-tb td { border: 1px solid #999; padding: 1px 4px; text-align: left; vertical-align: top; font-size: 9pt; }
.ds-tb th { background: #f1f1f1; font-size: 7.5pt; text-transform: uppercase; letter-spacing: .04em; }
.ds-tb td.num { text-align: right; font-weight: 700; }
.ds-tb td.time { font-weight: 700; white-space: nowrap; }
.ds-tb tr.large td { background: #fde7c4; }
.ds-tb tr.tea td { background: #fdf2f8; }
.ds-pkg { font-weight: 700; }
.ds-pkg small { font-weight: 400; color: #333; font-size: 8pt; margin-left: 4px; }
.ds-muted { color: #666; }
.ds-day { display: flex; flex-wrap: wrap; gap: 2px 12px; align-items: baseline; background: #1A5C3A; color: #fff; padding: 3px 6px; margin: 8px 0 3px; break-after: avoid; page-break-after: avoid; }
.ds-day h2 { font-size: 11.5pt; margin: 0; font-weight: 700; }
.ds-day span { font-size: 8.5pt; }
.ds-day span.ds-inhouse { font-weight: 700; }
.ds-tb tr.spa td { background: #ecfdf5; }
.ds-tb tr.evt td { background: #ede9fe; }
.ds-chip.evt { background: #6d28d9; }
.ds-foot { border-top: 2px solid #111; margin-top: 5px; padding-top: 2px; font-size: 8.5pt; display: flex; justify-content: space-between; }
.ds-empty { font-size: 9pt; color: #666; font-style: italic; margin: 0 0 3px; }
`;
